<template>
  <v-layout wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center>
        <v-flex xs12 sm4>
          <h1 class="headline">{{ $t('companyExpenses.title') }}</h1>
        </v-flex>

        <v-flex xs12 sm8>
          <v-layout mt-2 wrap align-center justify-end>
            <export-button
              v-if="$can('finance_expanse_export')"
              class="mr-2"
              large
              action-name="company-expenses/downloadReport"
              :file-name="$t('companyExpenses.title')"
              :query="filters"
            />
            <v-btn
              v-if="$can('finance_expanse_add')"
              class="mr-2"
              color="warning"
              large
              depressed
              @click="dialog = true"
            >
              {{ $t('common.button.add') }}
            </v-btn>
            <v-btn
              v-if="$can('finance_expanse_history_view')"
              class="mr-2"
              color="info"
              large
              depressed
              :to="{
                path: 'company-expenses/history',
                query: { dates: filters.dates },
              }"
            >
              {{ $t('investor-report.history-shortname') }}
            </v-btn>
            <v-btn
              v-if="$can('finance_expanse_config_view')"
              to="./company-expenses/configurator"
              color="blue-grey white--text"
              large
              depressed
            >
              {{ $t('finance.configurator') }}
            </v-btn>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-flex>

    <v-flex class="d-flex" mt-3 xs12>
      <v-menu
        ref="menuDate"
        :close-on-content-click="false"
        v-model="menuDate"
        :return-value.sync="filters.dates"
        max-width="280px"
        min-width="280px"
        class="pr-4 mt-2"
        transition="scale-transition"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-on="on"
            v-model="filters.dates"
            class="company-expenses__filter"
            prepend-icon="mdi-calendar"
            :label="$t('common.fields.rentDate')"
            readonly
            hide-details
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="filters.dates"
          color="primary lighten-1"
          width="280"
          range
          :locale="$i18n.locale"
          :selected-items-text="filters.dates.toString()"
          :allowed-dates="allowedDates"
          @input="changeDate"
        ></v-date-picker>
      </v-menu>

      <expense-type-select
        v-model="filters.type"
        class="company-expenses__filter"
        hide-details
        clearable
      />

      <type-category-select
        v-model="filters.categories"
        class="company-expenses__filter"
        multiple
        :type-id="filters.type"
        hide-details
      />

      <v-select
        v-model="filters.city"
        class="company-expenses__filter"
        item-text="name"
        item-value="id"
        :label="$t('finance.city')"
        :loading="citiesLoading"
        :items="cities"
        clearable
        hide-details
      />

      <v-select
        v-model="filters.office"
        class="company-expenses__filter"
        item-text="name"
        item-value="id"
        :label="$t('finance.office')"
        :items="offices"
        :disabled="!filters.city"
        clearable
        multiple
        hide-details
      />
    </v-flex>

    <v-flex class="d-flex align-center" xs12>
      <v-checkbox
        v-if="isAdmin"
        v-model="filters.withTrashed"
        color="warning"
        true-value="1"
        false-value="0"
        hide-details
        :label="$t('common.labels.showDeleted')"
      />
    </v-flex>

    <v-flex mt-5 xs12>
      <v-data-table
        ref="expenses-table"
        :height="tableHeight"
        :headers="headers"
        :items="list"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
      >
        <template #item="{ item, index }">
          <tr :class="{ 'deleted-row': item.deleted_at }">
            <td v-if="isAdmin" width="100">
              <v-btn
                v-if="item.deleted_at"
                class="restore-btn"
                color="warning"
                small
                icon
                :loading="restoreItems.includes(item.id)"
                @click="
                  () => {
                    confirmDialog.value = true;
                    confirmDialog.title = $t('common.restore') + ' ?';
                    confirmDialog.action = () => restoreItem(item.id);
                  }
                "
              >
                <v-icon small>mdi-restore</v-icon>
              </v-btn>
              <v-btn
                v-else
                color="red"
                small
                icon
                :loading="deletingItems.includes(item.id)"
                :disabled="item.deleted_at"
                @click="
                  () => {
                    confirmDialog.value = true;
                    confirmDialog.title = $t('common.btns.delete') + ' ?';
                    confirmDialog.action = () => deleteItem(item.id);
                  }
                "
              >
                <v-icon small>mdi-trash-can-outline</v-icon>
              </v-btn>
            </td>
            <td>{{ item.num }}</td>
            <td>
              <button
                class="edit-btn"
                :disabled="!$can('finance_expanse_edit')"
                @click="
                  () => {
                    dateEditDialog.value = true;
                    dateEditDialog.currentValue = item.date;
                    dateEditDialog.itemId = item.id;
                  }
                "
              >
                {{ item.date }}
              </button>
            </td>
            <td>
              <button
                class="edit-btn"
                :disabled="!$can('finance_expanse_edit')"
                @click="
                  () => {
                    updateTypeDialog.value = true;
                    updateTypeDialog.typeId = item.type.id;
                    updateTypeDialog.categoryId = item.category
                      ? item.category.id
                      : '';
                    updateTypeDialog.itemId = item.id;
                    updateTypeDialog.categoryOnly = false;
                  }
                "
              >
                {{ item.type.name }}
              </button>
            </td>
            <td>
              <button
                class="edit-btn"
                :disabled="!$can('finance_expanse_edit')"
                @click="
                  () => {
                    updateTypeDialog.value = true;
                    updateTypeDialog.typeId = item.type.id;
                    updateTypeDialog.categoryId = item.category
                      ? item.category.id
                      : '';
                    updateTypeDialog.itemId = item.id;
                    updateTypeDialog.categoryOnly = true;
                  }
                "
              >
                {{ item.category ? item.category.name : '—' }}
              </button>
            </td>
            <td>
              <button
                class="edit-btn"
                :disabled="!$can('finance_expanse_edit')"
                @click="
                  () => {
                    updateCityDialog.value = true;
                    updateCityDialog.cityId = item.office.city_id;
                    updateCityDialog.officeId = item.office_id;
                    updateCityDialog.itemId = item.id;
                    updateCityDialog.officeOnly = false;
                  }
                "
              >
                {{ item.office.city.name }}
              </button>
            </td>
            <td>
              <button
                class="edit-btn"
                :disabled="!$can('finance_expanse_edit')"
                @click="
                  () => {
                    updateCityDialog.value = true;
                    updateCityDialog.cityId = item.office.city_id;
                    updateCityDialog.officeId = item.office_id;
                    updateCityDialog.itemId = item.id;
                    updateCityDialog.officeOnly = true;
                  }
                "
              >
                {{ item.office.name }}
              </button>
            </td>
            <td>
              <button
                class="edit-btn"
                :disabled="!$can('finance_expanse_edit')"
                @click="
                  () => {
                    textFieldEditDialog.value = true;
                    textFieldEditDialog.paramName = 'description';
                    textFieldEditDialog.currentValue = item.description;
                    textFieldEditDialog.itemId = item.id;
                    textFieldEditDialog.type = 'text';
                  }
                "
              >
                {{ item.description || '—' }}
              </button>
            </td>
            <td>
              <button
                class="edit-btn"
                :disabled="!$can('finance_expanse_edit')"
                @click="
                  () => {
                    textFieldEditDialog.value = true;
                    textFieldEditDialog.paramName = 'amount';
                    textFieldEditDialog.currentValue = item.amount;
                    textFieldEditDialog.itemId = item.id;
                    textFieldEditDialog.type = 'number';
                  }
                "
              >
                {{ formatAmount(item.amount) }}
              </button>
            </td>
          </tr>

          <template v-if="index === list.length - 1">
            <tr class="expenses-table__total grey lighten-3">
              <td style="padding:0" :colspan="isAdmin ? 9 : 8">
                <table>
                  <tr>
                    <td>
                      <b>{{ $t('common.total') }}</b>
                    </td>
                    <td colspan="7" />
                    <td class="text-right">
                      <b>{{ formatAmount(expanseTotal) }}</b>
                    </td>
                  </tr>
                  <tr v-if="$can('finance_expanse_commission_view')">
                    <td>
                      <b>{{ $t('common.comission') + ' 20%' }}</b>
                    </td>
                    <td colspan="7" />
                    <td class="text-right">
                      <template v-if="comission !== null">
                        <v-btn
                          class="calculate-btn mr-2"
                          color="warning"
                          x-small
                          icon
                          :loading="comissionLoading"
                          @click="calculateComission"
                        >
                          <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                        <b>{{ formatAmount(comission) }}</b>
                      </template>
                      <v-btn
                        v-else
                        class="calculate-btn"
                        elevation="0"
                        color="info"
                        x-small
                        :loading="comissionLoading"
                        @click="calculateComission"
                      >
                        {{ $t('common.btns.calculate') }}
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-if="$can('finance_expanse_commission_view')">
                    <td>
                      <b>{{ $t('common.remainder') }}</b>
                    </td>
                    <td colspan="7" />
                    <td class="text-right">
                      <b v-if="comission !== null">
                        {{ formatAmount(balance) }}
                      </b>
                      <i class="balance-placeholder" v-else>{{
                        $t('finance.balancePlaceholder')
                      }}</i>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </template>
        </template>
      </v-data-table>
    </v-flex>

    <v-snackbar v-model="snack.value" :color="snack.color" bottom right>
      {{ snack.message }}
    </v-snackbar>

    <company-expenses-dialog v-model="dialog" @updated="processResult" />

    <update-type-dialog
      v-model="updateTypeDialog.value"
      :type-id="updateTypeDialog.typeId"
      :category-id="updateTypeDialog.categoryId"
      :item-id="updateTypeDialog.itemId"
      :category-only="updateTypeDialog.categoryOnly"
      @save="updateItem"
    />

    <update-city-dialog
      v-model="updateCityDialog.value"
      :city-id="updateCityDialog.cityId"
      :office-id="updateCityDialog.officeId"
      :item-id="updateCityDialog.itemId"
      :office-only="updateCityDialog.officeOnly"
      @save="updateItem"
    />

    <text-field-edit-dialog
      v-model="textFieldEditDialog.value"
      :param-name="textFieldEditDialog.paramName"
      :current-value="textFieldEditDialog.currentValue"
      :type="textFieldEditDialog.type"
      @save="updateItem({ id: textFieldEditDialog.itemId, params: $event })"
    />

    <date-edit-dialog
      v-model="dateEditDialog.value"
      :date="dateEditDialog.currentValue"
      @save="updateItem({ id: dateEditDialog.itemId, params: $event })"
    />

    <confirm-dialog
      v-model="confirmDialog.value"
      :title="confirmDialog.title"
      @confirm="confirmDialog.action"
    />
  </v-layout>
</template>

<script>
import { mapState } from 'vuex';
import checkRouteAccess from '@/helpers/checkRouteAccess';
import CompanyExpensesDialog from '@/components/finance/company-expenses/CompanyExpensesDialog.vue';
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue';
import calcTableHeight from '@/helpers/calcTableHeight';
import UpdateTypeDialog from '@/components/finance/company-expenses/UpdateTypeDialog.vue';
import ExpenseTypeSelect from '@/components/finance/company-expenses/ExpenseTypeSelect.vue';
import TypeCategorySelect from '@/components/finance/company-expenses/TypeCategorySelect.vue';
import formatAmount from '@/helpers/formatAmount';
import UpdateCityDialog from '@/components/finance/company-expenses/UpdateCityDialog.vue';
import TextFieldEditDialog from '@/components/shared/TextFieldEditDialog.vue';
import DateEditDialog from '@/components/shared/DateEditDialog.vue';
import ExportButton from '@/components/shared/ExportButton.vue';

export default {
  beforeRouteEnter(to, from, next) {
    checkRouteAccess('finance_expanse_view', next);
  },
  components: {
    CompanyExpensesDialog,
    ConfirmDialog,
    UpdateTypeDialog,
    ExpenseTypeSelect,
    TypeCategorySelect,
    UpdateCityDialog,
    TextFieldEditDialog,
    DateEditDialog,
    ExportButton,
  },
  data: () => ({
    loading: false,
    tableHeight: 'auto',
    dialog: false,
    menuDate: false,
    citiesLoading: false,
    deletingItems: [],
    restoreItems: [],
    comissionLoading: false,

    updateTypeDialog: {
      value: false,
      typeId: '',
      categoryId: '',
      itemId: '',
      categoryOnly: false,
    },

    updateCityDialog: {
      value: false,
      officeId: '',
      cityId: '',
      itemId: '',
      officeOnly: false,
    },

    textFieldEditDialog: {
      value: false,
      paramName: '',
      itemId: null,
      currentValue: '',
      type: 'text',
    },

    dateEditDialog: {
      value: false,
      itemId: false,
      currentValue: '',
    },

    filters: {
      withTrashed: 0,
      dates: [],
      type: '',
      categories: [],
      city: '',
      office: [],
    },

    snack: {
      value: false,
      color: null,
      message: null,
    },

    confirmDialog: {
      value: false,
      title: null,
      action: () => {},
    },
  }),
  watch: {
    filters: {
      deep: true,
      handler(params) {
        if (params.dates.length === 1) return;
        this.fetchList(params);
      },
    },
    'filters.city': {
      handler(val) {
        if (!val) this.filters.office = [];
      },
      'filters.type': {
        handler(val) {
          if (!val) this.filters.categories = [];
        },
      },
    },
  },
  computed: {
    ...mapState({
      list: (state) => state['company-expenses'].list,
      cities: (state) => state['company-expenses'].citiesList,
      comission: (state) => state['company-expenses'].comission,
      expanseTotal: (state) => state['company-expenses'].expanseTotal,
    }),
    headers() {
      const items = [
        { text: this.$t('common.number'), sortable: false },
        { text: this.$t('common.fields.bookingDate'), sortable: false },
        { text: this.$t('finance.expenceType'), sortable: false },
        { text: this.$t('finance.category'), sortable: false },
        { text: this.$t('finance.city'), sortable: false },
        { text: this.$t('finance.office'), sortable: false },
        { text: this.$t('common.table.description'), sortable: false },
        { text: this.$t('common.fields.sum'), sortable: false },
      ];

      if (this.isAdmin) items.unshift({ text: '', sortable: false });

      return items;
    },
    isAdmin() {
      return this.$store.getters['global/getRole'] === 'admin';
    },
    offices() {
      const currentCity = this.cities.find(
        ({ id }) => id === this.filters.city,
      );

      return currentCity ? currentCity.offices : [];
    },
    today() {
      const date = new Date();
      const formatNum = (v) => (v < 10 ? `0${v}` : v);

      return `${date.getFullYear()}-${formatNum(
        date.getMonth() + 1,
      )}-${date.getDate()}`;
    },
    balance() {
      return this.comission - this.expanseTotal;
    },
  },
  methods: {
    formatAmount,
    changeDate(val) {
      if (val.length === 2) {
        this.$refs.menuDate.save(this.filters.dates);
      }
    },
    allowedDates(value) {
      const [from, to] = this.filters.dates;

      if (!from) return true;
      if (to) return true;

      const firstDate = new Date(from);
      const nextDate = new Date(value);

      return nextDate >= firstDate;
    },
    async fetchList(params) {
      this.loading = true;
      await this.$store.dispatch('company-expenses/fetchList', params);
      this.loading = false;
    },
    processResult({ success, error }) {
      if (success) {
        this.fetchList(this.filters);
        this.dialog = false;
      }

      const snack = {
        value: true,
        color: 'success',
        message: this.$t('common.saved'),
      };

      if (error) {
        snack.color = 'error';
        snack.message = error || this.$t('common.errors.APIerror');
      }

      this.snack = snack;
    },
    async fetchCities() {
      this.citiesLoading = true;
      await this.$store.dispatch('company-expenses/fetchCities');
      this.citiesLoading = false;
    },
    async deleteItem(id) {
      this.deletingItems.push(id);
      const result = await this.$store.dispatch(
        'company-expenses/deleteItem',
        id,
      );

      this.processResult(result);
      this.deletingItems = this.deletingItems.filter((item) => item !== id);
    },
    async restoreItem(id) {
      this.restoreItems.push(id);
      const result = await this.$store.dispatch(
        'company-expenses/restoreItem',
        id,
      );

      this.processResult(result);
      this.deletingItems = this.restoreItems.filter((item) => item !== id);
    },
    async updateItem(params) {
      const result = await this.$store.dispatch(
        'company-expenses/updateItem',
        params,
      );

      this.updateTypeDialog.value = false;
      this.updateCityDialog.value = false;
      this.dateEditDialog.value = false;

      this.processResult(result);
    },
    async calculateComission() {
      this.comissionLoading = true;
      await this.$store.dispatch(
        'company-expenses/fetchComission',
        this.filters,
      );
      this.comissionLoading = false;
    },
  },
  created() {
    this.filters.dates = [this.today, this.today];
    this.fetchCities();
  },
  mounted() {
    const table = this.$refs['expenses-table'];
    this.tableHeight = calcTableHeight(table.$el);
  },
};
</script>

<style lang="scss">
.expenses-table__total {
  position: sticky;
  bottom: 0;
}

.company-expenses__per-page {
  max-width: 150px;
  margin-left: auto;
}

.company-expenses__filter {
  max-width: 220px;
  width: 100%;

  &:not(:last-child) {
    margin-right: 25px;
  }
}

.edit-btn {
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #464646;
  line-height: 1.3;
  text-decoration: none;

  &[disabled] {
    font-weight: normal;
  }

  &:not([disabled]):hover {
    opacity: 0.7;
  }
}

.calculate-btn {
  .v-progress-circular {
    height: 15px !important;
  }
}

.balance-placeholder {
  color: #777;
}
</style>
